import { 
  updatePassword,
  reauthenticateWithCredential,
  EmailAuthProvider
} from 'firebase/auth';
import { 
  collection,
  doc, 
  updateDoc, 
  deleteDoc,
  getDoc,
  getDocs,
  query,
  where
} from 'firebase/firestore';
import { auth, db } from '../config/firebase';

export const userService = {
  // Update user password
  async updatePassword(currentPassword, newPassword) {
    try {
      const user = auth.currentUser;
      if (!user) {
        throw new Error('No authenticated user found');
      }

      // Re-authenticate user with current password
      const credential = EmailAuthProvider.credential(user.email, currentPassword);
      await reauthenticateWithCredential(user, credential);

      // Update password
      await updatePassword(user, newPassword);

      // Update user document to mark password as changed
      const userQuery = query(
        collection(db, 'users'),
        where('uid', '==', user.uid)
      );
      const userSnapshot = await getDocs(userQuery);
      if (!userSnapshot.empty) {
        await updateDoc(doc(db, 'users', userSnapshot.docs[0].id), {
          passwordChanged: true,
          updatedAt: new Date()
        });
      }

      return true;
    } catch (error) {
      console.error('Error updating password:', error);
      throw error;
    }
  },

  // Check if user needs to change password (for tenants)
  async checkPasswordStatus() {
    try {
      const user = auth.currentUser;
      if (!user) {
        return { needsPasswordChange: false };
      }

      // First try to get user document by UID as document ID (for tenants)
      try {
        const userDocRef = doc(db, 'users', user.uid);
        const userDocSnap = await getDoc(userDocRef);
        
        if (userDocSnap.exists()) {
          const userData = userDocSnap.data();
          return {
            needsPasswordChange: !userData.passwordChanged,
            role: userData.role
          };
        }
      } catch (docError) {
        console.log('User document not found by UID as document ID, trying query...');
      }

      // If not found by document ID, try querying by uid field (for other users)
      const userQuery = query(
        collection(db, 'users'),
        where('uid', '==', user.uid)
      );
      const userSnapshot = await getDocs(userQuery);
      
      if (!userSnapshot.empty) {
        const userData = userSnapshot.docs[0].data();
        return {
          needsPasswordChange: !userData.passwordChanged,
          role: userData.role
        };
      }

      return { needsPasswordChange: false };
    } catch (error) {
      console.error('Error checking password status:', error);
      return { needsPasswordChange: false };
    }
  },

  // Get user profile data
  async getUserProfile() {
    try {
      const user = auth.currentUser;
      if (!user) {
        throw new Error('No authenticated user found');
      }

      // First try to get user document by UID as document ID (for tenants)
      try {
        const userDocRef = doc(db, 'users', user.uid);
        const userDocSnap = await getDoc(userDocRef);
        
        if (userDocSnap.exists()) {
          return { id: userDocSnap.id, ...userDocSnap.data() };
        }
      } catch (docError) {
        console.log('User document not found by UID as document ID, trying query...');
      }

      // If not found by document ID, try querying by uid field (for other users)
      const userQuery = query(
        collection(db, 'users'),
        where('uid', '==', user.uid)
      );
      const userSnapshot = await getDocs(userQuery);
      
      if (!userSnapshot.empty) {
        return { id: userSnapshot.docs[0].id, ...userSnapshot.docs[0].data() };
      }

      throw new Error('User profile not found');
    } catch (error) {
      console.error('Error getting user profile:', error);
      throw error;
    }
  },

  // Update user profile
  async updateUserProfile(updateData) {
    try {
      const user = auth.currentUser;
      if (!user) {
        throw new Error('No authenticated user found');
      }

      // First try to update user document by UID as document ID (for tenants)
      try {
        const userDocRef = doc(db, 'users', user.uid);
        const userDocSnap = await getDoc(userDocRef);
        
        if (userDocSnap.exists()) {
          await updateDoc(userDocRef, {
            ...updateData,
            updatedAt: new Date()
          });
          return true;
        }
      } catch (docError) {
        console.log('User document not found by UID as document ID, trying query...');
      }

      // If not found by document ID, try querying by uid field (for other users)
      const userQuery = query(
        collection(db, 'users'),
        where('uid', '==', user.uid)
      );
      const userSnapshot = await getDocs(userQuery);
      
      if (!userSnapshot.empty) {
        await updateDoc(doc(db, 'users', userSnapshot.docs[0].id), {
          ...updateData,
          updatedAt: new Date()
        });
        return true;
      }

      throw new Error('User profile not found');
    } catch (error) {
      console.error('Error updating user profile:', error);
      throw error;
    }
  },

  // Get all users (for admin dashboard)
  async getAllUsers() {
    try {
      const querySnapshot = await getDocs(collection(db, 'users'));
      const users = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      // Sort by creation date (newest first)
      return users.sort((a, b) => b.createdAt?.toDate?.() - a.createdAt?.toDate?.());
    } catch (error) {
      console.error('Error getting all users:', error);
      throw error;
    }
  },

  // Get user by ID
  async getUserById(userId) {
    try {
      const docRef = doc(db, 'users', userId);
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        return { id: docSnap.id, ...docSnap.data() };
      } else {
        throw new Error('User not found');
      }
    } catch (error) {
      console.error('Error getting user by ID:', error);
      throw error;
    }
  },

  // Update user (for admin editing)
  async updateUser(userId, updateData) {
    try {
      console.log('Updating user with ID:', userId, 'Data:', updateData);
      
      const docRef = doc(db, 'users', userId);
      await updateDoc(docRef, {
        ...updateData,
        updatedAt: new Date()
      });
      
      console.log('User updated successfully');
      return { id: userId, ...updateData };
    } catch (error) {
      console.error('Error updating user:', error);
      throw error;
    }
  },

  // Get users by role
  async getUsersByRole(role) {
    try {
      const q = query(
        collection(db, 'users'),
        where('role', '==', role)
      );
      const querySnapshot = await getDocs(q);
      const users = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      return users.sort((a, b) => b.createdAt?.toDate?.() - a.createdAt?.toDate?.());
    } catch (error) {
      console.error('Error getting users by role:', error);
      throw error;
    }
  },

  // Delete user
  async deleteUser(userId) {
    try {
      console.log('Deleting user with ID:', userId);
      
      // Get user data first to check if it's a tenant
      const userDoc = await getDoc(doc(db, 'users', userId));
      if (!userDoc.exists()) {
        throw new Error('User not found');
      }
      
      const userData = userDoc.data();
      
      // If this is a tenant, also delete the tenant document
      if (userData.role === 'tenant' && userData.uid) {
        try {
          // Find and delete tenant document
          const tenantQuery = query(
            collection(db, 'tenants'),
            where('authUid', '==', userData.uid)
          );
          const tenantSnapshot = await getDocs(tenantQuery);
          if (!tenantSnapshot.empty) {
            await deleteDoc(doc(db, 'tenants', tenantSnapshot.docs[0].id));
            console.log('Tenant document deleted');
          }
        } catch (error) {
          console.warn('Error deleting tenant document:', error);
        }
      }
      
      // Delete user document
      await deleteDoc(doc(db, 'users', userId));
      console.log('User document deleted successfully');
      
      return true;
    } catch (error) {
      console.error('Error deleting user:', error);
      throw error;
    }
  }
}; 